import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Smile } from 'lucide-react';
import { cn } from '../../lib/cn';
import { QUICK_PICK_EMOJIS } from '../../services/messaging/messageReactionService';
import { getOwnerDocument, shouldDismissOverlay } from '../../store/sidecarBehavior';
import { EmojiPicker } from './EmojiPicker';

interface Props {
  onSelect: (emoji: string) => void;
  onClose: () => void;
  isOwn: boolean;
  /** Boutons d'action (Répondre / Modifier / Supprimer) rendus à droite des emotes. */
  actions?: ReactNode;
}

/**
 * Menu contextuel d'un message (ouvert au clic droit) : emojis rapides,
 * bouton picker complet, et boutons d'action. Se ferme au clic extérieur ou Échap.
 */
export function MessageReactionBar({ onSelect, onClose, isOwn, actions }: Props) {
  const [showFullPicker, setShowFullPicker] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const doc = getOwnerDocument(node, document);
    function onPointerDown(e: MouseEvent) {
      if (shouldDismissOverlay(node, e.target as Node | null)) onClose();
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    doc.addEventListener('mousedown', onPointerDown);
    doc.addEventListener('keydown', onKeyDown);
    return () => {
      doc.removeEventListener('mousedown', onPointerDown);
      doc.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return (
    <div
      ref={ref}
      className={cn(
        'absolute -bottom-8 z-10 flex items-center gap-0.5 px-1 py-0.5 rounded-full',
        'bg-white border border-cime-border shadow-md',
        isOwn ? 'right-2' : 'left-2',
      )}
    >
      {QUICK_PICK_EMOJIS.map((emoji) => (
        <button
          key={emoji}
          type="button"
          onClick={() => onSelect(emoji)}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-cime-surface transition-colors text-base"
          aria-label={`Réagir avec ${emoji}`}
          title={emoji}
        >
          {emoji}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setShowFullPicker((v) => !v)}
        className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-cime-surface transition-colors text-cime-text-muted"
        aria-label="Plus d'emojis"
        title="Plus d'emojis"
      >
        <Smile className="w-4 h-4" />
      </button>

      {actions && (
        <>
          <span className="w-px h-5 bg-cime-border mx-0.5" />
          {actions}
        </>
      )}

      {showFullPicker && (
        <div className={cn('absolute bottom-full mb-1', isOwn ? 'right-0' : 'left-0')}>
          <EmojiPicker
            onSelect={(emoji) => {
              setShowFullPicker(false);
              onSelect(emoji);
            }}
            onClose={() => setShowFullPicker(false)}
          />
        </div>
      )}
    </div>
  );
}
